import { Hidden } from '@mui/material'
import Image from 'next/image'
import Link from 'next/link'
import { IEnum_Componentcontentcontentwriter_Writeroption } from 'src/graphql/generated/hooks'
import { useContentWriterInformation } from 'src/hooks/useContentWriterInformation'
import type { NormalizedWriter } from 'src/utils/normalizeContentWriters'

import { CompatibleLink } from '../CompatibleLink/CompatibleLink'

interface ContentWritersBylineProps {
  writers: NormalizedWriter[]
  variant?: 'compact' | 'detail'
}

/** Same `<p>` typography as single-author `ContentWriter` (avatar spacing uses `ml-[5px]` on names only). */
export const COMPACT_LIST_AUTHOR_NAME_P_CLASS =
  'font-primary font-bold text-[15px] leading-[18px] mr-[0.5rem] ml-[5px] text-darkgray'

/** Byline segment name (margin lives on the segment wrapper so mobile avatars keep spacing). */
const COMPACT_LIST_BYLINE_NAME_CLASS = 'font-primary font-bold text-[15px] leading-[18px] ml-[5px] text-darkgray'

const WRITER_COMPACT_SEGMENT_CLASS = 'flex shrink-0 flex-row items-center md:mr-[0.5rem]'

const WriterCompactSegment: React.FC<{ writer: NormalizedWriter }> = ({ writer }) => {
  const info = useContentWriterInformation(writer)
  if (!info) return null
  const { name, option, logo, url } = info

  const photoAvatar = (
    <>
      {option !== IEnum_Componentcontentcontentwriter_Writeroption.Vendor && (
        <Image
          src={logo}
          alt=""
          className="h-[26px] w-[26px] shrink-0 rounded-[13px]"
          width={26}
          height={26}
          priority
        />
      )}
      {option === IEnum_Componentcontentcontentwriter_Writeroption.Vendor && (
        <div className="shrink-0 rounded-[3px] bg-clouds p-[2px]">
          <Image src={logo} alt="" className="h-[26px] w-[26px] rounded-[13px]" priority width={26} height={26} />
        </div>
      )}
    </>
  )

  const inner = (
    <>
      {photoAvatar}
      <Hidden mdDown>
        <p className={COMPACT_LIST_BYLINE_NAME_CLASS}>{name}</p>
      </Hidden>
    </>
  )

  return url ? (
    <CompatibleLink href={url} className={WRITER_COMPACT_SEGMENT_CLASS} title={name}>
      {inner}
    </CompatibleLink>
  ) : (
    <div className={WRITER_COMPACT_SEGMENT_CLASS} title={name} aria-label={name}>
      {inner}
    </div>
  )
}

const WriterDetailSegment: React.FC<{ writer: NormalizedWriter }> = ({ writer }) => {
  const info = useContentWriterInformation(writer)
  if (!info?.name) return null
  const { name, url } = info
  return url ? (
    <Link href={url} className="resource-normal-link underline cursor-pointer" style={{ fontSize: '18px' }}>
      {name}
    </Link>
  ) : (
    <span className="resource-normal-link underline cursor-pointer" style={{ fontSize: '18px' }}>
      {name}
    </span>
  )
}

/** Up to two writers; detail uses linked names with “and”; compact shows avatar + name per author. */
export const ContentWritersByline: React.FC<ContentWritersBylineProps> = ({ writers, variant = 'compact' }) => {
  const list = writers.slice(0, 2).filter(Boolean)
  if (list.length === 0) return null

  if (variant === 'detail') {
    return (
      <span className="inline-flex flex-row flex-wrap items-baseline">
        <WriterDetailSegment writer={list[0]} />
        {list.length > 1 && (
          <>
            <span className="resource-desc shrink-0 whitespace-pre" style={{ fontSize: '18px' }} aria-hidden>
              {' and '}
            </span>
            <WriterDetailSegment writer={list[1]} />
          </>
        )}
      </span>
    )
  }

  return (
    <span className="inline-flex shrink-0 flex-row flex-nowrap items-center gap-x-1 md:gap-x-0">
      <WriterCompactSegment writer={list[0]} />
      {list.length > 1 && <WriterCompactSegment writer={list[1]} />}
    </span>
  )
}
